import React, { forwardRef } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '../../utils/cn'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg' | 'icon'
  loading?: boolean
  asChild?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading = false, disabled, children, ...props }, ref) => {
    const baseStyles = [
      'inline-flex items-center justify-center rounded-lg font-medium transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50'
    ]

    const variants = {
      default: [
        'bg-primary text-primary-foreground shadow-sm',
        'hover:bg-primary/90',
        'focus-visible:ring-ring',
        'active:scale-95 transition-transform'
      ],
      primary: [
        'bg-primary text-primary-foreground shadow-sm',
        'hover:bg-primary/90',
        'focus-visible:ring-ring',
        'active:scale-95 transition-transform'
      ],
      secondary: [
        'bg-secondary text-secondary-foreground shadow-sm',
        'hover:bg-secondary/80',
        'focus-visible:ring-ring',
        'active:scale-95 transition-transform'
      ],
      outline: [
        'border border-input bg-background text-foreground shadow-sm',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:ring-ring',
        'active:scale-95 transition-transform'
      ],
      ghost: [
        'text-foreground',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:ring-ring',
        'active:scale-95 transition-transform'
      ],
      danger: [
        'bg-destructive text-destructive-foreground shadow-sm',
        'hover:bg-destructive/90',
        'focus-visible:ring-ring',
        'active:scale-95 transition-transform'
      ]
    }

    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-6 py-3 text-base',
      icon: 'h-10 w-10'
    }

    return (
      <button
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          className
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }